<template>
  <div>
    <v-label
      class="mb-2"
      :class="{ error: errors?.length }"
    >
      <FieldLabel :field="field" />
    </v-label>

    <div class="files">
      <BorderedTile
        :excluded="field.readonly"
        @click="innerInput?.click()"
      >
        <div class="text-center text-caption">
          <vi-icon
            icon="mdi:image-search-outline"
            :size="50"
          />

          <br>
          Add media
        </div>

        <VFileInput
          ref="innerInput"
          :accept="getAcceptType(field)"
          class="inner-file-input"
          :disabled="field.readonly"
          :multiple="field.multiple"
          @update:model-value="updateFiles($event)"
        />

        <template #caption>
          Click to add media
        </template>
      </BorderedTile>

      <FilePreview
        v-for="(file, i) in innerModelValue"
        :key="isFile(file) ? `${file.name}_${i}` : file.id"
        class="media-tile"
        :excluded="isMarkedForDeletion(file)"
        :file="file"
        :menu-actions="getMenuActions(file)"
        :menu-title="getMenuTitle(file)"
        :selected="isMainValue(file)"
        @click:action="onMenuActionClicked($event, i)"
        @click:dismiss="removeFile(i)"
      />
    </div>

    <v-btn
      v-show="showResetChanges"
      class="mt-4 mb-2"
      color="primary"
      flat
      @click="resetChanges()"
    >
      <template #prepend>
        <vi-icon icon="mdi:rotate-left" />
      </template>

      Discard Media Changes
    </v-btn>

    <v-messages
      :active="!!errors?.length"
      class="mb-2"
      color="error"
      :messages="errors"
    />
  </div>
</template>

<script setup lang="ts">
import { VFileInput } from 'vuetify/components';

const props = defineProps<{
  field: FieldConfig
  rules?: FormRule[]
  errors?: string[]
  dense?: boolean
  mainValue?: File | MediaAsset
  modelValue: File | MediaAsset | (File | MediaAsset)[] | undefined
}>();
const emit = defineEmits(['update:modelValue', 'update:mainValue']);
const { promptForConfirmation } = useConfirmationDialogStore();
const innerInput = ref<InstanceType<typeof VFileInput>>();
const innerModelValue = ref<(File | MediaAsset)[]>(ensureArray(props.modelValue));
const originalValue = ref(Array.isArray(props.modelValue) ? props.modelValue.slice() : props.modelValue);
const originalMainValue = ref(props.mainValue);
const mainValueRef = toRef(props, 'mainValue');
const modelValueRef = toRef(props, 'modelValue');
const originalValueArr = computed(() => ensureArray(originalValue.value));
const modelValueArr = computed(() => ensureArray(modelValueRef.value));

/**
 * Gets the index of a given file in a given list by checking file equality.
 *
 * @param file
 * @param list
 */
const getFileIndexInList = (file: File | MediaAsset, list: (File | MediaAsset)[]) =>
  list.findIndex(f => areFilesEqual(f, file));

/**
 * Picks a new main value from the given files, provided that it meets the restrictions for
 * the main value.
 * TODO: allow the mainValue restrictions to be set in the field itself
 *
 * @param files
 */
const setNewMainValue = (files: (File | MediaAsset)[]) =>
  emit('update:mainValue', files.find(file => getFileTypePrefix(file) === 'image') || null);

/**
 * Determines whether or not the "Discard Media Changes" button should be shown
 */
const showResetChanges = computed(() => {
  const mainValueChanged = mainValueRef.value !== originalMainValue.value;
  if (props.field.multiple) {
    // TODO: deep comparison between new and old array values
    const fileCountChanged = Array.isArray(originalValue.value) && modelValueArr.value.length !== originalValueArr.value.length;
    return fileCountChanged || mainValueChanged;
  }

  return modelValueRef.value !== originalValue.value || mainValueChanged;
});

/**
 * Clears all new files from the model value and restores the original value
 */
const resetChanges = async () => {
  const response = await promptForConfirmation(`Are you sure you want to reset all changes to ${props.field.label}? This will remove any files that have not yet been uploaded.`);
  if (response) {
    emit('update:modelValue', originalValue.value);
    emit('update:mainValue', originalMainValue.value);
    innerModelValue.value = ensureArray(originalValue.value);
  }
};

/**
 * Removes the file at the specified index in the innerModelValue from the
 * modelValue itself, while keeping the file in the innerModelValue so
 * that it can be re-added if desired.
 *
 * @param index
 */
const removeFile = async (index: number) => {
  const actualIndex = getFileIndexInList(innerModelValue.value[index], modelValueArr.value);
  const newValueArr = modelValueArr.value.slice();
  const [removedFile] = newValueArr.splice(actualIndex, 1);
  emit('update:modelValue', newValueArr);

  if (
    props.field.mainValueKey &&
      areFilesEqual(mainValueRef.value || null, removedFile)
  ) {
    // If the previously selected main image was just removed,
    // select the first file of the remaining files
    setNewMainValue(newValueArr);
  }
};

/**
 * Re-adds the media at the specified index in the modelValue, then emits an update
 * for the modelValue (including new files).
 *
 * @param index
 */
const reAddFile = (file: File | MediaAsset, index: number) => {
  const newModelValueArr = modelValueArr.value.slice();
  newModelValueArr.splice(index, 0, file);
  emit('update:modelValue', newModelValueArr);
};

/**
 * Adds the newFiles array to the model value.
 * Called whenever the value of the hidden input is updated, so that the
 * previously selected files are left selected rather than replaced.
 *
 * @param newFiles
 */
const updateFiles = (newFiles: File[]) => {
  if (props.field.multiple) {
    emit('update:modelValue', [...modelValueArr.value, ...newFiles]);
    innerModelValue.value = [...innerModelValue.value, ...newFiles];
    if (props.field.mainValueKey && !mainValueRef.value) {
      setNewMainValue(newFiles);
    }
  }
  else {
    emit('update:modelValue', newFiles[0]);
  }
};

/**
 * Returns true if the provided file or media asset is the currently selected main value.
 *
 * @param file
 */
const isMainValue = (file: File | MediaAsset) => areFilesEqual(file, mainValueRef.value || null);

/**
 * Returns true if the file is not present in the current modelValue
 *
 * @param file
 */
const isMarkedForDeletion = (file: File | MediaAsset) => {
  return getFileIndexInList(file, modelValueArr.value) === -1;
};

/**
 * Gets the title to display for the file's tile menu
 *
 * @param file
 */
const getMenuTitle = (file: File | MediaAsset) => {
  const typePrefix = getFileTypePrefix(file);
  const fileIsSelected = isMainValue(file);
  return `${fileIsSelected ? 'main ' : ''}${typePrefix} options`;
};

/**
 * Gets the actions for the file's tile menu
 *
 * @param file
 */
const getMenuActions = (file: File | MediaAsset) => {
  const menuActions: ButtonConfigMap = {};
  const typePrefix = getFileTypePrefix(file);
  const fileIsSelected = isMainValue(file);
  const markedForDeletion = isMarkedForDeletion(file);

  if (typePrefix === 'image') {
    if (fileIsSelected) {
      menuActions.unselect = {
        text: 'Unset cover photo',
        icon: 'mdi:star-outline',
      };
    }
    else if (!markedForDeletion) {
      menuActions.select = {
        text: 'Set as cover photo',
        icon: 'mdi:star',
      };
    }
  }

  if (markedForDeletion) {
    menuActions.undelete = {
      text: 'Undo delete',
      icon: 'mdi:rotate-left',
    };
  }
  else {
    menuActions.delete = {
      text: `Delete ${typePrefix}`,
      icon: 'mdi:trash-can-outline',
    };
  }

  return menuActions;
};

/**
 * Updates the main value for this field
 *
 * @param file
 */
const updateMainValue = (file: File | MediaAsset | null) => {
  emit('update:mainValue', file);
};

/**
 * Handles when a menu action is clicked for either a new file or an existing file
 *
 * @param $event
 */
const onMenuActionClicked = ({ action, file }: { action: string; file: File | MediaAsset }, index: number) => {
  switch (action) {
    case 'select': {
      updateMainValue(file);
      break;
    }

    case 'unselect': {
      updateMainValue(null);
      break;
    }

    case 'delete': {
      removeFile(index);
      break;
    }

    case 'undelete': {
      reAddFile(file, index);
      break;
    }
  }
};

/**
 * Returns the value for the "accept-type" property for this field.
 *
 * @param field
 */
const getAcceptType = (field: FieldConfig) => {
  if (field.accept) {
    return field.accept;
  }
  return field.type === 'media' ? 'image/*,video/*' : undefined;
};

/**
 * Resets the field as if it were just created, using the current
 * modelValue as the new originalValue
 */
const reset = async () => {
  await nextTick(); // make sure props have been updated before this is called from parent
  originalValue.value = Array.isArray(props.modelValue) ? props.modelValue.slice() : props.modelValue;
  originalMainValue.value = mainValueRef.value;
  innerModelValue.value = ensureArray(props.modelValue);
};

defineExpose({ reset });
</script>

<style scoped lang="scss">
@use "sass:map";
@import "~/assets/styles/variables";

:deep(.v-field__input) {
  z-index: 2; /* Place the file previews above the input itself */
}

.files {
  display: grid;
  flex-wrap: wrap;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.inner-file-input {
  position: absolute;
  opacity: 0;
}

.v-label.error {
  color: rgb(var(--v-theme-error));
}

@media (map.get($display-breakpoints, 'sm-and-up')) {
  .files {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;
  }
}

@media (map.get($display-breakpoints, 'md-and-up')) {
  .files {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
}
</style>
